import React, { useEffect, useState } from 'react';

interface ThumbnailProps {
  src: string;
  alt: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onClick?: () => void;
}

const sizeClasses = {
  sm: 'w-12 h-12',
  md: 'w-16 h-16',
  lg: 'w-20 h-20',
};

export const WAITING_FRAMES = ['-', '\\', '|', '/'];

export const WaitingThumbnail: React.FC<{ sizeClass: string; className: string; onClick?: () => void }> = ({ sizeClass, className, onClick }) => {
  const [frame, setFrame] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setFrame((f) => (f + 1) % WAITING_FRAMES.length);
    }, 150);
    return () => clearInterval(timer);
  }, []);

  return (
    <div
      onClick={onClick}
      className={`${sizeClass} relative rounded overflow-hidden bg-black border border-dashed border-yellow-500/40 flex flex-col items-center justify-center font-mono text-yellow-500/80 ${onClick ? 'cursor-pointer hover:border-yellow-500/80 hover:bg-yellow-500/5 transition-colors' : ''} ${className}`}
    >
      <div className="text-[8px] uppercase tracking-widest mb-0.5 opacity-70">wait</div>
      <div className="text-[11px] font-bold">[{WAITING_FRAMES[frame]}]</div>
    </div>
  );
};

export const FailedThumbnail: React.FC<{ sizeClass: string; className: string; onClick?: () => void }> = ({ sizeClass, className, onClick }) => {
  return (
    <div
      onClick={onClick}
      className={`${sizeClass} relative rounded overflow-hidden bg-black border border-dashed border-red-500/40 flex flex-col items-center justify-center font-mono text-red-500/80 ${onClick ? 'cursor-pointer hover:border-red-500/80 hover:bg-red-500/5 transition-colors' : ''} ${className}`}
    >
      <div className="text-[8px] uppercase tracking-widest mb-0.5 opacity-70">fail</div>
      <div className="text-[11px] font-bold">[!]</div>
    </div>
  );
};

const Thumbnail: React.FC<ThumbnailProps> = ({
  src,
  alt,
  size = 'md',
  className = '',
  onClick,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (retryCount > 0 && retryCount <= 3) {
      const timer = setTimeout(() => {
        setHasError(false);
        setIsLoading(true);
      }, retryCount * 1000);
      return () => clearTimeout(timer);
    }
  }, [retryCount]);

  if (src && src.includes('_waiting')) {
    return <WaitingThumbnail sizeClass={sizeClasses[size]} className={className} onClick={onClick} />;
  }

  if (src && src.includes('_failed')) {
    return <FailedThumbnail sizeClass={sizeClasses[size]} className={className} onClick={onClick} />;
  }

  return (
    <div
      className={`${sizeClasses[size]} relative rounded overflow-hidden bg-slate-700 ${onClick ? 'cursor-pointer hover:opacity-80' : ''} ${className}`}
      onClick={onClick}
    >
      {isLoading && !hasError && (
        <div className="absolute inset-0 bg-slate-600 animate-pulse flex items-center justify-center">
          <span className="text-slate-500 text-xs">⏳</span>
        </div>
      )}

      {hasError && (
        <div className="absolute inset-0 bg-slate-700 flex items-center justify-center">
          <span className="text-slate-500 text-xs">❌</span>
        </div>
      )}

      <img
        key={`${src}-${retryCount}`}
        src={retryCount > 0 ? `${src}${src.includes('?') ? '&' : '?'}retry=${retryCount}` : src}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-200 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        loading="lazy"
        decoding="async"
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setIsLoading(false);
          if (retryCount < 3) {
            setRetryCount((c) => c + 1);
          } else {
            setHasError(true);
          }
        }}
      />
    </div>
  );
};

export default Thumbnail;